import React, { SyntheticEvent, useEffect } from 'react';
import { generatePath } from 'react-router';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/Components/shadcn';
import { Skeleton } from '@/Components/shadcn/skeleton';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';
import { CandidateTabs } from '@/Models/CandidateOverview';
import useApplicantAssessmentCounters from '@/Pages/Candidates/AiSearch/api/useApplicantAssessmentCounters';
import useAISearchCounters from '@/Pages/Candidates/AiSearch/api/useAISearchCounters';
import useAISearchProgress from '@/Pages/Candidates/AiSearch/api/useAISearchProgress';

interface ProfileDetailsSidebarProps {
    assessmentId: string;
    clientId: string;
    companyId: string;
    isAgenticSearchSelected: boolean;
    isIdentifiedAiSearchSelected: boolean;
    isShortlistedAiSearchSelected: boolean;
    selectedApplicantOverviewTab?: CandidateTabs;
    handleApplicantOverviewTabChange?: (event: SyntheticEvent, value: CandidateTabs) => void;
}

const CandidatesOverviewNavigationSidebar = ({
    assessmentId,
    clientId,
    companyId,
    isAgenticSearchSelected,
    isIdentifiedAiSearchSelected,
    isShortlistedAiSearchSelected,
    selectedApplicantOverviewTab,
    handleApplicantOverviewTabChange,
}: ProfileDetailsSidebarProps) => {
    const {
        data: aiSearchCountersData,
        isLoading: isAISearchCountersLoading,
        mutate: mutateAiSearchCounter,
    } = useAISearchCounters();
    const identifiedCount = aiSearchCountersData?.totalIdentified;
    const shortlistedCount = aiSearchCountersData?.totalShortlisted;

    const { data: applicantAssessmentCountersData, isLoading: isApplicantAssessmentCountersLoading } =
        useApplicantAssessmentCounters();
    const openCount = applicantAssessmentCountersData?.totalOpen;
    const hiredCount = applicantAssessmentCountersData?.totalHired;
    const rejectedCount = applicantAssessmentCountersData?.totalRejected;

    const { data: aiSearchProgress } = useAISearchProgress();

    useEffect(() => {
        if (aiSearchProgress?.isInProgress === false) {
            mutateAiSearchCounter();
        }
    }, [aiSearchProgress, mutateAiSearchCounter]);

    const handleTabChangeWrapper = (event: SyntheticEvent, nextTab: CandidateTabs): void => {
        if (handleApplicantOverviewTabChange) {
            handleApplicantOverviewTabChange(event, nextTab);
        }
    };

    const isApplicantOverviewPage =
        !isIdentifiedAiSearchSelected && !isShortlistedAiSearchSelected && !isAgenticSearchSelected;
    const applicantOverviewTabs: Array<CandidateTabs> = [
        CandidateTabs.Open,
        CandidateTabs.Hired,
        CandidateTabs.Rejected,
    ];

    const getApplicantOverviewCounterByTab = (tabName: CandidateTabs): string => {
        switch (tabName) {
            case CandidateTabs.Open:
                return `${UIStrings.Open} ${openCount ?? ''}`;
            case CandidateTabs.Hired:
                return `${UIStrings.Hired} ${hiredCount ?? ''}`;
            case CandidateTabs.Rejected:
                return `${UIStrings.Rejected} ${rejectedCount ?? ''}`;
            default:
                return '';
        }
    };

    return (
        <>
            <div className="mb-6">
                <SidebarGroup className="p-0">
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <Link
                                        role="navigation"
                                        data-cy="backButton"
                                        to={generatePath(RoutePath.AssessmentsActive, { clientId, companyId })}
                                        className="text-muted-foreground flex items-center gap-2"
                                    >
                                        <ChevronLeft className="h-4 w-4 shrink-0" />
                                        <span className="text-sm font-semibold">{UIStrings.Back}</span>
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </div>

            <div className="mb-6" data-cy="aiSearchList">
                <SidebarGroup className="p-0">
                    <SidebarGroupLabel className="text-muted-foreground px-0 text-xs tracking-wide uppercase">
                        {UIStrings.AISearch}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    isActive={isAgenticSearchSelected}
                                    className="h-auto max-h-8 min-h-8 py-1.5"
                                >
                                    <Link
                                        to={generatePath(RoutePath.AgenticAiSearch, { assessmentId, companyId })}
                                        className="flex w-full items-center justify-between gap-2"
                                    >
                                        <span className="truncate">{UIStrings.AgenticSearch}</span>
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </div>

            <div className="mb-6" data-cy="candidatesList">
                <SidebarGroup className="p-0">
                    <SidebarGroupLabel className="text-muted-foreground px-0 text-xs tracking-wide uppercase">
                        {UIStrings.Candidates}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    isActive={isIdentifiedAiSearchSelected}
                                    className="h-auto max-h-8 min-h-8 py-1.5"
                                >
                                    <Link
                                        to={generatePath(RoutePath.IdentifiedAiSearch, { assessmentId, companyId })}
                                        className="flex w-full items-center justify-between gap-2"
                                    >
                                        <span className="truncate">
                                            {`${UIStrings.Identified} ${identifiedCount ?? ''}`}
                                        </span>
                                        {isAISearchCountersLoading && <Skeleton className="h-4 w-[3ch] shrink-0" />}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    isActive={isShortlistedAiSearchSelected}
                                    className="h-auto max-h-8 min-h-8 py-1.5"
                                >
                                    <Link
                                        to={generatePath(RoutePath.ShortlistedAiSearch, { assessmentId, companyId })}
                                        className="flex w-full items-center justify-between gap-2"
                                    >
                                        <span className="truncate">
                                            {`${UIStrings.Shortlisted} ${shortlistedCount ?? ''}`}
                                        </span>
                                        {isAISearchCountersLoading && <Skeleton className="h-4 w-[3ch] shrink-0" />}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </div>

            <div data-cy="applicantsList">
                <SidebarGroup className="p-0">
                    <SidebarGroupLabel className="text-muted-foreground px-0 text-xs tracking-wide uppercase">
                        {UIStrings.Applicants}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {isApplicantOverviewPage
                                ? applicantOverviewTabs.map((tabName) => (
                                      <SidebarMenuItem key={tabName}>
                                          <SidebarMenuButton
                                              type="button"
                                              isActive={selectedApplicantOverviewTab === tabName}
                                              className="h-auto max-h-8 min-h-8 py-1.5"
                                              onClick={(e) => handleTabChangeWrapper(e, tabName)}
                                          >
                                              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                                                  <span className="truncate">
                                                      {getApplicantOverviewCounterByTab(tabName)}
                                                  </span>
                                                  {isApplicantAssessmentCountersLoading && (
                                                      <Skeleton className="h-4 w-[3ch] shrink-0" />
                                                  )}
                                              </span>
                                          </SidebarMenuButton>
                                      </SidebarMenuItem>
                                  ))
                                : applicantOverviewTabs.map((tabName) => (
                                      <SidebarMenuItem key={tabName}>
                                          <SidebarMenuButton
                                              asChild
                                              isActive={selectedApplicantOverviewTab === tabName}
                                              className="h-auto max-h-8 min-h-8 py-1.5"
                                          >
                                              <Link
                                                  to={generatePath(`${RoutePath.CandidatesOverview}?tab=${tabName}`, {
                                                      assessmentId,
                                                      companyId,
                                                  })}
                                                  className="flex w-full items-center justify-between gap-2"
                                              >
                                                  <span className="truncate">
                                                      {getApplicantOverviewCounterByTab(tabName)}
                                                  </span>
                                                  {isApplicantAssessmentCountersLoading && (
                                                      <Skeleton className="h-4 w-[3ch] shrink-0" />
                                                  )}
                                              </Link>
                                          </SidebarMenuButton>
                                      </SidebarMenuItem>
                                  ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </div>
        </>
    );
};

export default CandidatesOverviewNavigationSidebar;
